import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { engApi } from './engApi';
import { todayJst } from '@/utils/jstDate';

const TITLES = { use: 'プロジェクトで使用', scrap: 'スクラップ登録', return: '在庫へ返却',
  receive: '入庫（数量を増やす）', adjust: '在庫調整（数え直した数）' };

// mode: 'use' | 'scrap' | 'return'
export const EngAssetActionDialog = ({ open, onClose, asset, mode, onDone }) => {
  const [qty, setQty] = useState(1);
  const [projectId, setProjectId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [reason, setReason] = useState('');
  const [fromUse, setFromUse] = useState(false);
  const [note, setNote] = useState('');
  const [reqNo, setReqNo] = useState('');
  const [atDate, setAtDate] = useState(todayJst());
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQty(1); setProjectId(''); setProjectName(''); setPurpose(''); setReason(''); setFromUse(false); setNote('');
    setReqNo(''); setAtDate(todayJst());
    if (mode === 'adjust') setQty(asset?.qty_total ?? 0);
    if (mode === 'use') engApi.projects().then(setProjects).catch(() => {});
  }, [open, mode, asset?.qty_total]);

  if (!asset) return null;
  const avail = asset.qty_available ?? 0;
  const used = asset.qty_used ?? 0;
  const stockModes = mode === 'receive' || mode === 'adjust';
  const maxQty = stockModes ? Number.MAX_SAFE_INTEGER
    : (mode === 'return' ? used : (mode === 'scrap' && fromUse ? used : avail));

  const submit = async () => {
    const n = Number(qty);
    if (!(n >= 0) || (mode !== 'adjust' && !(n > 0))) { toast.error('数量を入力してください'); return; }
    if (mode === 'adjust' && !reason.trim()) { toast.error('在庫調整には理由が必要です'); return; }
    if (n > maxQty) { toast.error(`数量は最大 ${maxQty} までです`); return; }
    if (mode === 'scrap' && !reason.trim()) { toast.error('スクラップ理由は必須です'); return; }
    setSaving(true);
    try {
      let res;
      if (stockModes) {
        res = await engApi.receiveAsset(asset.id, {
          ...(mode === 'adjust' ? { set_qty: n } : { qty: n }),
          reason: reason.trim() || null, at_date: atDate || null,
          request_no: reqNo.trim() || null,
        });
      } else if (mode === 'use') {
        res = await engApi.useAsset(asset.id, {
          qty: n, project_id: projectId || null,
          project_name: projectId ? null : (projectName.trim() || null),
          purpose: purpose.trim() || null,
        });
      } else if (mode === 'scrap') {
        res = await engApi.scrapAsset(asset.id, { qty: n, reason: reason.trim(), from_use: fromUse });
      } else {
        res = await engApi.returnAsset(asset.id, { qty: n, note: note.trim() || null });
      }
      toast.success('登録しました');
      onDone?.(res);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '登録に失敗しました');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="eng-action-dialog">
        <DialogHeader>
          <DialogTitle>{TITLES[mode]} — {asset.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {mode === 'scrap' && used > 0 && (
            <div className="flex items-center justify-between rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
              <Label className="text-xs font-semibold text-zinc-700">使用中の分をスクラップ（故障など）</Label>
              <Switch checked={fromUse} onCheckedChange={setFromUse} data-testid="eng-action-from-use" />
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold text-zinc-600">
              {mode === 'adjust' ? `数え直した在庫数 ＊（今 ${asset.qty_total ?? 0}${asset.uom || '個'}）`
                : mode === 'receive' ? `入庫する数 ＊（${asset.uom || '個'}）`
                  : `数量 ＊（最大 ${maxQty}${asset.uom || '個'}）`}
            </Label>
            <Input type="number" min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="h-10 mt-1 tabular-nums" data-testid="eng-action-qty" />
          </div>
          {stockModes && (
            <>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">
                  理由 {mode === 'adjust' ? '＊' : '（任意）'}
                </Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder={mode === 'adjust' ? '棚卸しで数え直した など' : '購入分の入庫 など'}
                  className="h-10 mt-1" data-testid="eng-action-stock-reason" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">日付</Label>
                  <Input type="date" value={atDate} onChange={(e) => setAtDate(e.target.value)}
                    className="h-10 mt-1" data-testid="eng-action-stock-date" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">関連REQ（任意）</Label>
                  <Input value={reqNo} onChange={(e) => setReqNo(e.target.value)}
                    placeholder="REQ-000017" className="h-10 mt-1" data-testid="eng-action-stock-req" />
                </div>
              </div>
            </>
          )}
          {mode === 'use' && (
            <>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">プロジェクト</Label>
                <select value={projectId} onChange={(e) => setProjectId(e.target.value)}
                  className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-teal-400"
                  data-testid="eng-action-project">
                  <option value="">（選択しない / 下に直接入力）</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              {!projectId && (
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">プロジェクト名（直接入力）</Label>
                  <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} className="h-10 mt-1" data-testid="eng-action-project-name" />
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold text-zinc-600">用途・メモ</Label>
                <Textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={2} className="mt-1" data-testid="eng-action-purpose" />
              </div>
            </>
          )}
          {mode === 'scrap' && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">理由 ＊</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="例: 動作不良のため廃棄" className="mt-1" data-testid="eng-action-reason" />
            </div>
          )}
          {mode === 'return' && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">メモ</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1" data-testid="eng-action-note" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={submit} disabled={saving}
            className={mode === 'scrap' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-teal-700 hover:bg-teal-800'}
            data-testid="eng-action-submit">
            {saving ? '登録中…' : '登録'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
